// Operator and group pickers shared by Send to (console) and Reassign (wallboard).

import { useEffect, useState } from 'react';
import { api, ApiError, type Group, type Presence, type PresenceStatus } from '../api.ts';
import { PRESENCE_LABEL } from './presence.tsx';

/** Offline, or no console stream: cannot be picked. */
export function isOffline(p: Presence): boolean {
  return p.status === 'offline' || !p.online;
}

export function shownStatus(p: Presence): PresenceStatus {
  return isOffline(p) ? 'offline' : p.status;
}

export function presencePillCls(st: PresenceStatus): string {
  return st === 'available' ? 'ok' : st === 'busy' ? 'warn' : st === 'offline' ? 'bad' : '';
}

export function PresenceTag({ p }: { p: Presence }) {
  const st = shownStatus(p);
  return <span className={`pill ${presencePillCls(st)}`}>{PRESENCE_LABEL[st]}</span>;
}

function loadText(p: Presence): string {
  return `${p.activeCount}/${p.cap} active${p.parkedCount ? ` · ${p.parkedCount} parked` : ''}`;
}

function rank(p: Presence): number {
  const st = shownStatus(p);
  return st === 'available' ? 0 : st === 'busy' ? 1 : st === 'break' ? 2 : 3;
}

export function sortPeople(list: Presence[]): Presence[] {
  return list.slice().sort((a, b) => rank(a) - rank(b) || a.activeCount - b.activeCount || a.displayName.localeCompare(b.displayName));
}

/** Radio list of operators: status, load against the cap, groups and skills. Offline people are disabled. */
export function OperatorOptions({
  list,
  selected,
  onSelect,
  overCapOk = false,
}: {
  list: Presence[];
  selected: string | null;
  onSelect: (userId: string) => void;
  /** Reassign may go over the cap: show it as a warning, not a block. */
  overCapOk?: boolean;
}) {
  if (list.length === 0) return <div className="note">No other operators.</div>;
  return (
    <div role="radiogroup" aria-label="Operators" className="optlist">
      {sortPeople(list).map((p) => {
        const off = isOffline(p);
        const atCap = p.activeCount >= p.cap;
        const groups = p.groups
          .slice()
          .sort((a, b) => Number(b.primary) - Number(a.primary))
          .map((g) => g.name);
        const meta = [groups.join(', '), p.skills.join(', '), loadText(p)].filter(Boolean).join(' · ');
        return (
          <button
            key={p.userId}
            type="button"
            role="radio"
            aria-checked={selected === p.userId}
            className={`opt${selected === p.userId ? ' on' : ''}`}
            disabled={off}
            title={off ? 'Offline: no console open, so they cannot take it' : undefined}
            onClick={() => onSelect(p.userId)}
          >
            <span className="rd" aria-hidden="true" />
            <span>
              {p.displayName}
              {p.routingPaused && <span className="pill warn tiny-pill">routing paused</span>}
              <small>
                {meta}
                {atCap && !off && <span className={overCapOk ? 'warn-t' : 'dim'}> · at cap{overCapOk ? ' (reassign still goes through)' : ''}</span>}
              </small>
            </span>
            <PresenceTag p={p} />
          </button>
        );
      })}
    </div>
  );
}

/** Groups for Send to. GET /api/setup/groups is for supervisors and admins; others see the groups named in presence. */
export function useTransferGroups(people: Presence[] | undefined): { groups: Group[] | undefined; error: unknown } {
  const [state, setState] = useState<{ groups: Group[] | undefined; error: unknown; derive: boolean }>({ groups: undefined, error: null, derive: false });
  useEffect(() => {
    const ctl = new AbortController();
    api.groups(ctl.signal).then(
      (g) => setState({ groups: g.filter((x) => x.active), error: null, derive: false }),
      (e: unknown) => {
        if (ctl.signal.aborted) return;
        if (e instanceof ApiError && (e.status === 403 || e.status === 401)) setState({ groups: undefined, error: null, derive: true });
        else setState({ groups: undefined, error: e, derive: false });
      },
    );
    return () => ctl.abort();
  }, []);
  if (!state.derive) return { groups: state.groups, error: state.error };
  if (!people) return { groups: undefined, error: null };
  const m = new Map<string, Group>();
  for (const p of people) {
    for (const g of p.groups) {
      const cur = m.get(g.id) ?? { id: g.id, name: g.name, kind: 'queue', overflowGroupId: null, overflowAfterS: 0, acceptTimeoutS: 0, active: true, members: [] };
      cur.members.push({ userId: p.userId, displayName: p.displayName, primary: g.primary });
      m.set(g.id, cur);
    }
  }
  return { groups: [...m.values()].sort((a, b) => a.name.localeCompare(b.name)), error: null };
}

export function GroupOptions({ groups, people, selected, onSelect }: { groups: Group[]; people: Presence[]; selected: string | null; onSelect: (groupId: string) => void }) {
  if (groups.length === 0) return <div className="note">No groups set up. An admin adds them in Setup → Groups and routing.</div>;
  const byId = new Map(people.map((p) => [p.userId, p]));
  return (
    <div role="radiogroup" aria-label="Groups" className="optlist">
      {groups.map((g) => {
        const avail = g.members.filter((m) => {
          const p = byId.get(m.userId);
          return p && shownStatus(p) === 'available' && !p.routingPaused;
        }).length;
        return (
          <button key={g.id} type="button" role="radio" aria-checked={selected === g.id} className={`opt${selected === g.id ? ' on' : ''}`} onClick={() => onSelect(g.id)}>
            <span className="rd" aria-hidden="true" />
            <span>
              {g.name}
              <small>
                {g.kind} · {g.members.length} member{g.members.length === 1 ? '' : 's'} · <span className={avail ? 'ok-t' : 'warn-t'}>{avail} available</span>
              </small>
            </span>
            <span />
          </button>
        );
      })}
    </div>
  );
}
